import React, { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { createAppTheme } from './createAppTheme';

const DEFAULT_BRANDING = {
  primaryColor: null,
  accentColor: null,
  fontFamily: null,
  fontUrl: null,
  logo: null,
  slogan: null,
  fontSizeScale: 1,
};

const BrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  setBranding: () => {},
  resetBranding: () => {},
  brandingLoading: true,
  setBrandingLoading: () => {},
});

/**
 * Holds branding state + builds the MUI theme from it. This provider does
 * NOT decide which tenant's branding to load — that requires knowing the
 * authenticated user's active tenant, which is resolved by `AuthContext`
 * (hostname lookup, login response, or `/:tenantId/*` route param). See
 * `BrandingSync` in `App.jsx`, which is the single source of truth that
 * fetches branding whenever `activeTenantId` changes and pushes it here via
 * `setBranding`. Previously this provider guessed the tenant from the URL
 * path on its own (independently of AuthContext), which broke for every
 * bare top-level route (e.g. `/campaigns`, `/employees`) not in its
 * hardcoded route list — the lookup silently failed and branding stayed on
 * DEFAULT_BRANDING, causing tenant styles to randomly fall back to default.
 */
export function BrandColorProvider({ children }) {
  const [branding, setBrandingState] = useState(DEFAULT_BRANDING);
  const [brandingLoading, setBrandingLoading] = useState(true);

  const setBranding = useCallback((patch) => {
    setBrandingState((prev) => ({
      ...prev,
      ...(typeof patch === 'function' ? patch(prev) : patch),
    }));
  }, []);

  const resetBranding = useCallback(() => {
    setBrandingState(DEFAULT_BRANDING);
  }, []);

  useEffect(() => {
    const scale = typeof branding.fontSizeScale === 'number' ? branding.fontSizeScale : 1;
    document.documentElement.style.fontSize = `${scale * 100}%`;
  }, [branding.fontSizeScale]);

  useEffect(() => {
    const fontUrl = branding.fontUrl;
    if (!fontUrl) return;
    const id = 'branding-font-stylesheet';
    let link = document.getElementById(id);
    if (!link) {
      link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = fontUrl;
    return () => {
      if (link && link.parentNode) link.parentNode.removeChild(link);
    };
  }, [branding.fontUrl]);

  const theme = useMemo(() => createAppTheme('light', branding), [branding]);
  const contextValue = useMemo(
    () => ({ branding, setBranding, resetBranding, brandingLoading, setBrandingLoading }),
    [branding, setBranding, resetBranding, brandingLoading]
  );

  return (
    <BrandingContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
